import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import {
  Notification,
  NotificationInboxStatus,
  NotificationListParams,
  NotificationsResponse,
  NotificationsUnreadCountResponse,
} from '@/models/notification'
import api from '@/shared/api'
import { ENDPOINTS } from '@/shared/constants'

type NotificationsState = {
  items: Notification[]
  total: number
  page: number
  pageSize: number
  unreadCount: number
  loading: boolean
  selectedNotification: Notification | null
  inboxStatus: NotificationInboxStatus
}

const initialState: NotificationsState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 20,
  unreadCount: 0,
  loading: false,
  selectedNotification: null,
  inboxStatus: 'all',
}

function matchesCurrentInboxStatus(
  notification: Notification,
  inboxStatus: NotificationInboxStatus
) {
  if (inboxStatus === 'archived') {
    return Boolean(notification.isArchived)
  }

  if (inboxStatus === 'unread') {
    return !notification.isRead && !notification.isArchived
  }

  return !notification.isArchived
}

export const fetchNotifications = createAsyncThunk<
  NotificationsResponse,
  NotificationListParams | undefined
>('notifications/fetchNotifications', async (params, thunkAPI) => {
  try {
    const response = await api.get<NotificationsResponse>(
      ENDPOINTS.NOTIFICATIONS.GET.ALL,
      { params }
    )
    return response.data
  } catch (error) {
    toast.error('Erro ao listar avisos')
    return thunkAPI.rejectWithValue(error)
  }
})

export const fetchUnreadNotificationsCount =
  createAsyncThunk<number>('notifications/fetchUnreadCount', async (_, thunkAPI) => {
    try {
      const response = await api.get<NotificationsUnreadCountResponse>(
        ENDPOINTS.NOTIFICATIONS.GET.UNREAD_COUNT
      )
      return response.data.count
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  })

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    upsertNotification(state, action: PayloadAction<Notification>) {
      const index = state.items.findIndex((item) => item.id === action.payload.id)

      if (index === -1) {
        if (!matchesCurrentInboxStatus(action.payload, state.inboxStatus)) {
          return
        }

        state.items.unshift(action.payload)
        state.total += 1
      } else {
        if (!matchesCurrentInboxStatus(action.payload, state.inboxStatus)) {
          state.items.splice(index, 1)
          state.total = Math.max(0, state.total - 1)
          return
        }

        state.items[index] = {
          ...state.items[index],
          ...action.payload,
        }
      }

      if (state.selectedNotification?.id === action.payload.id) {
        state.selectedNotification = {
          ...state.selectedNotification,
          ...action.payload,
        }
      }
    },
    removeNotification(state, action: PayloadAction<number>) {
      const existing = state.items.find((item) => item.id === action.payload)
      state.items = state.items.filter((item) => item.id !== action.payload)

      if (existing) {
        state.total = Math.max(0, state.total - 1)
      }

      if (state.selectedNotification?.id === action.payload) {
        state.selectedNotification = null
      }
    },
    setUnreadCount(state, action: PayloadAction<number>) {
      state.unreadCount = Math.max(0, action.payload)
    },
    incrementUnreadCount(state) {
      state.unreadCount += 1
    },
    decrementUnreadCount(state) {
      state.unreadCount = Math.max(0, state.unreadCount - 1)
    },
    setSelectedNotification(
      state,
      action: PayloadAction<Notification | null>
    ) {
      state.selectedNotification = action.payload
    },
    updateNotificationReadStatus(state, action: PayloadAction<Notification>) {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id
      )
      const currentNotification =
        index !== -1 ? state.items[index] : state.selectedNotification
      const readStatusChanged =
        currentNotification?.isRead !== action.payload.isRead

      if (index !== -1) {
        if (state.inboxStatus === 'unread' && action.payload.isRead) {
          state.items.splice(index, 1)
          state.total = Math.max(0, state.total - 1)
        } else {
          state.items[index] = action.payload
        }
      } else if (
        state.inboxStatus === 'unread' &&
        !action.payload.isRead &&
        !action.payload.isArchived
      ) {
        state.items.unshift(action.payload)
        state.total += 1
      }

      if (readStatusChanged && !action.payload.isArchived) {
        state.unreadCount = Math.max(
          0,
          state.unreadCount + (action.payload.isRead ? -1 : 1)
        )
      }

      if (state.selectedNotification?.id === action.payload.id) {
        state.selectedNotification = action.payload
      }
    },
    clearNotifications(state) {
      state.items = []
      state.total = 0
      state.page = 1
      state.selectedNotification = null
      state.unreadCount = 0
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items
        state.total = action.payload.total
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
        state.inboxStatus = action.meta.arg?.status ?? 'all'
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.loading = false
      })
      .addCase(fetchUnreadNotificationsCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload
      })
  },
})

export const {
  upsertNotification,
  removeNotification,
  setUnreadCount,
  incrementUnreadCount,
  decrementUnreadCount,
  setSelectedNotification,
  updateNotificationReadStatus,
  clearNotifications,
} = notificationsSlice.actions

export default notificationsSlice.reducer
